import { onScopeDispose, ref, watch, type Ref } from 'vue'
import type { PDataTableRowIdValue } from '../types'

export const DEFAULT_HIGHLIGHT_DURATION = 4000

type UseRowHighlightOptions = {
  highlightedRowIds: Ref<PDataTableRowIdValue[]>
  highlightDuration: Ref<number>
}

export type UseRowHighlightReturn = {
  isRowIdHighlighted: (rowId: PDataTableRowIdValue) => boolean
}

export const useRowHighlight = ({
  highlightedRowIds,
  highlightDuration,
}: UseRowHighlightOptions): UseRowHighlightReturn => {
  const activeRowIds = ref<Set<PDataTableRowIdValue>>(new Set())

  let timeout: ReturnType<typeof setTimeout> | null = null

  const clearPendingTimeout = (): void => {
    if (timeout === null) {
      return
    }

    clearTimeout(timeout)
    timeout = null
  }

  watch(
    [highlightedRowIds, highlightDuration],
    ([rowIds, duration]) => {
      clearPendingTimeout()
      activeRowIds.value = new Set(rowIds)

      if (rowIds.length === 0 || duration <= 0) {
        return
      }

      timeout = setTimeout(() => {
        activeRowIds.value = new Set()
        timeout = null
      }, duration)
    },
    { immediate: true },
  )

  onScopeDispose(clearPendingTimeout)

  const isRowIdHighlighted = (rowId: PDataTableRowIdValue): boolean => activeRowIds.value.has(rowId)

  return {
    isRowIdHighlighted,
  }
}
